import { FadeIn } from "@/components/FadeIn";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";

const anchors = [
  { id: "overview", label: "Overview" },
  { id: "orientation", label: "Orientation" },
  { id: "self-inquiry", label: "Self-Inquiry" },
  { id: "education", label: "Education" },
  { id: "ecology", label: "Earth & Ecology" },
  { id: "entrepreneurship", label: "Social Entrepreneurship" },
  { id: "expressions", label: "Current Expressions" },
  { id: "invitation", label: "Invitation" },
];

export default function TheWorkPage() {
  return (
    <div data-testid="the-work-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground text-center">
            The Work
          </h1>
        </FadeIn>
      </section>

      {/* Anchor Nav */}
      <nav className="sticky top-16 md:top-20 z-40 border-b border-[hsl(48,12%,88%)] px-6 md:px-12 lg:px-24 overflow-x-auto" style={{ backgroundColor: "hsla(48,45%,98%,0.9)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-screen-xl mx-auto flex gap-6 py-3">
          {anchors.map((a) => (
            <a
              key={a.id}
              href={`#${a.id}`}
              className="font-body text-xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors duration-300"
              data-testid={`anchor-${a.id}`}
            >
              {a.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Overview */}
        <section id="overview" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>
                The work emerges from observing human life and its crises as they are: conflict, suffering, division, and contradiction. It does not begin with solutions, beliefs, or ideologies, but with attention. From observation, responses take form. Sometimes as dialogue. Sometimes as education. Sometimes as living experiments.
              </p>
              <p>There is no fixed mission. The work remains open and evolves with understanding.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Orientation */}
        <section id="orientation" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">Orientation</h2>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>Human beings have made immense outward progress, from fire to AI. Yet inwardly, we remain burdened by fear, fragmentation, and conflict. War, climate breakdown, mental distress, and broken relationships are not isolated problems. They reflect a deeper crisis in human consciousness.</p>
              <p>Rather than trying to reform the world only externally, the work explores whether change at the root, within the human mind, can give rise to a different way of living.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Self-Inquiry */}
        <section id="self-inquiry" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">Self-Inquiry and Inner Transformation</h2>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>Inquiry into the psychological structure of the "I" including fear, desire, conditioning, and conflict. This includes self-observation, mental health, relationships, and understanding suffering without escaping from it.</p>
              <p>The work explores whether oneness, love, and compassion can flower naturally, contributing positively to human consciousness in a time of crisis.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Education */}
        <section id="education" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">Alternative Education and Living</h2>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>Exploring learning beyond conditioning through alternative education, conscious living, and community-based experiments where awareness is part of daily life.</p>
              <blockquote className="font-accent italic text-lg md:text-xl text-foreground pl-6 border-l-2 border-[#5C6B5D] my-6">Can education liberate rather than condition? Can it nurture love, compassion, and non-fragmented human beings?</blockquote>
              <p>Education and living are not separate. Experiments include decentralized JDF Community Learning Centers and Life Education and Development programs in schools and colleges.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Ecology */}
        <section id="ecology" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">Earth and Ecology</h2>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>Engagement with climate change, regenerative economy, localization, and natural farming. The work questions humanity's relationship with the earth and explores whether responsible living can arise from awareness rather than regulation.</p>
              <p>Recognizing that the earth is finite, the work also questions patterns of consumption. Recent efforts include JDF Climate Advocacy programs.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Social Entrepreneurship */}
        <section id="entrepreneurship" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">Social Entrepreneurship and Innovation</h2>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>Social Entrepreneurship as a goodness for welfare of the world, focusing on localisation, and solving critical problems through creativity and innovation.</p>
              <p>This includes work toward Gram Swaraj by strengthening villages to become self-sustaining through local resources and addressing mass migration from rural to urban areas. Ongoing experiments in Bihar include capacity building, skill development, and livelihood initiatives.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Current Expressions */}
        <section id="expressions" className="py-24 md:py-32 scroll-mt-32">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 text-foreground">Current Expressions</h2>
            <div className="space-y-8">
              {[
                { name: "Jeevan Darshan Foundation (JDF)", desc: "Working across self-inquiry, education, mental health, ecology, and conscious living." },
                { name: "JDF Community Learning Centers (JCLC)", desc: "Decentralized learning spaces integrating education and life." },
                { name: "JDF Tapovan Ashram (Upcoming)", desc: "A permanent residential campus dedicated to inner transformation, self-inquiry, conscious living, and ecological harmony." },
                { name: "School of Self-Observation", desc: "A community-led initiative by JDF dedicated to self-awareness, wisdom, and genuine spirituality." },
                { name: "Retreats and Workshops", desc: "Shared inquiry spaces, not methods or techniques." },
                { name: "Dialogues and Writings", desc: "Spaces for questioning, listening, and collective exploration." },
                { name: "Education and Climate Initiatives", desc: "Programs developed with JDF and partner organizations." },
              ].map((item, i) => (
                <div key={i} className="font-body" data-testid={`expression-${i}`}>
                  <h4 className="text-base text-foreground mb-1">{item.name}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
              <p className="font-body text-sm text-muted-foreground italic">Each remains open, evolving, and non-institutional in spirit.</p>
            </div>
          </FadeIn>
        </section>

        <Separator style={{ backgroundColor: "#E5E4DE" }} />

        {/* Open Invitation */}
        <section id="invitation" className="py-24 md:py-32 scroll-mt-32 text-center">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">An Open Invitation</h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground mb-8 max-w-xl mx-auto">
              This work is for those who feel the urgency to look at life directly, without escape or comfort in ready-made conclusions. Participation, collaboration, or dialogue is possible, not as followers, but as fellow inquirers.
            </p>
            <Link
              to="/dialogue"
              className="inline-block font-body text-sm tracking-[0.15em] uppercase px-8 py-3 border border-[#5C6B5D] text-[#5C6B5D] hover:bg-[#5C6B5D] hover:text-white transition-all duration-300"
              data-testid="work-dialogue-link"
            >
              Begin a Dialogue
            </Link>
          </FadeIn>
        </section>
      </div>
    </div>
  );
}
